import React from "react";
import {Box, Typography} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {DeclineReason, OpenRequestFeedbackDTO} from "@/api/data-contracts";
import DeclineReasonSelect from "@/common/request-cards/DeclineReasonSelect";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {
    buildFeedbackRequestLabel,
    buildRequestFeedbackDeepLinkUrl
} from "@/pages/login-page/feedback/feedback-dialog-logic";
import {PostLoginFeedbackDialogI18N} from "@/translations";

/**
 * Props for a single stacked feedback entry block — a pure presentation component. It owns no state;
 * the parent {@code PostLoginFeedbackDialog} holds the entry-state map and passes the current
 * reason/freeText/shareWithBidder down, receiving changes back keyed by {@code feedbackId}.
 */
interface FeedbackEntryBlockProps {
    feedback: OpenRequestFeedbackDTO;
    reason: DeclineReason | undefined;
    freeText: string | null;
    shareWithBidder: boolean;
    onReasonChange: (reason: DeclineReason) => void;
    onFreeTextChange: (freeText: string) => void;
    onShareWithBidderChange: (shareWithBidder: boolean) => void;
}

/**
 * One visually delimited block within the stacked post-login feedback dialog: the request identifier
 * as a deep-link heading plus the shared {@link DeclineReasonSelect} (RadioGroup over all seven reasons
 * — including NO_REASON_GIVEN as a regular, directly selectable option — and the dynamic
 * optional/mandatory free-text field).
 */
const FeedbackEntryBlock: React.FC<FeedbackEntryBlockProps> = ({
    feedback,
    reason,
    freeText,
    shareWithBidder,
    onReasonChange,
    onFreeTextChange,
    onShareWithBidderChange
}) => {
    const {t} = useTranslation();

    const label = buildFeedbackRequestLabel(feedback);
    const deepLinkUrl =
        feedback.requestId !== undefined && feedback.requestId !== null
            ? buildRequestFeedbackDeepLinkUrl(window.location.origin, window.location.pathname, feedback.requestId)
            : undefined;

    return (
        <Box
            sx={{border: 1, borderColor: "divider", borderRadius: 1, padding: 2, marginBottom: 2}}
            data-cy={`feedback-entry-block-${feedback.feedbackId}`}
        >
            <Typography variant="subtitle1" gutterBottom>
                {deepLinkUrl ? (
                    <a
                        href={deepLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t(PostLoginFeedbackDialogI18N.deepLinkAriaLabel, {label})}
                    >
                        {label}
                        <OpenInNew fontSize="inherit" sx={{verticalAlign: "middle", marginLeft: 0.5}} />
                    </a>
                ) : (
                    label
                )}
            </Typography>
            <DeclineReasonSelect
                reason={reason}
                freeText={freeText}
                onReasonChange={onReasonChange}
                onFreeTextChange={onFreeTextChange}
                freeTextFieldName={`freeText-${feedback.feedbackId}`}
            />
            <CheckboxInputField
                name={`shareWithBidder-${feedback.feedbackId}`}
                label={t(PostLoginFeedbackDialogI18N.shareWithBidderLabel)}
                value={shareWithBidder}
                onChange={({value}) => onShareWithBidderChange(value === true)}
            />
        </Box>
    );
};

export default FeedbackEntryBlock;
